import { useState, useEffect, useCallback } from "react";
import { useOrder } from "@/context";
import { SELECTED_ORDER_ID_KEY } from "@/context";
import { zendeskRequest, zafClient } from "@/services/zendesk";
import type { ZafMetadata } from '@/interfaces';

export interface ChildTicket {
  id: number;
  subject: string;
  status: string;
}

interface SearchResponse {
  results: ChildTicket[];
  count: number;
}

export const TICKET_CREATED_EVENT = "vtex:ticket:created";

export function useInformacoes() {
  const { order, conversation, documents } = useOrder();
  const [childTickets, setChildTickets] = useState<ChildTicket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  const fetchChildTickets = useCallback(async () => {
    const orderId = localStorage.getItem(SELECTED_ORDER_ID_KEY);
    if (!orderId || !zafClient) return;

    setLoadingTickets(true);
    try {
      // Obtém o ID do campo dinâmico do manifesto (configurações do app)
      const meta = await zafClient.metadata() as unknown as ZafMetadata;
      const fieldId = meta.settings.orderIDField;

      if (!fieldId) {
        console.warn("[Informacoes] orderIDField não configurado no painel de instalação.");
        setLoadingTickets(false);
        return;
      }

      // Busca por tickets que possuem o orderId no campo customizado dinâmico
      const data = await zendeskRequest<SearchResponse>({
        url: `/api/v2/search.json?query=custom_field_${fieldId}:${encodeURIComponent(orderId)}+type:ticket`,
        type: "GET",
      });
      setChildTickets(data.results ?? []);
    } catch (error) {
      console.error("[Informacoes] Erro ao buscar tickets filhos:", error);
      setChildTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  }, []);

  useEffect(() => {
    async function load() {
      await fetchChildTickets();
    }
    load();
  }, []);

  // Atualiza a lista imediatamente após a criação de um novo ticket filho
  useEffect(() => {
    window.addEventListener(TICKET_CREATED_EVENT, fetchChildTickets);
    return () => window.removeEventListener(TICKET_CREATED_EVENT, fetchChildTickets);
  }, [fetchChildTickets]);

  return { order, conversation, childTickets, loadingTickets, documents };
}
